import React, { useCallback, useMemo, useState } from "react"
import { observer } from "mobx-react-lite"
import { ActivityIndicator, Alert, Pressable, View, ViewStyle, TextStyle } from "react-native"
import { StackScreenProps } from "@react-navigation/stack"
import { Feather } from "@expo/vector-icons"
import { useQuery } from "@apollo/client"

import { Button, Screen, Text, useModal } from "@app/components"
import { textPresets } from "@app/components/button/button.presets"
import { color, spacing } from "@app/theme"
import { RootNavigatorParamList } from "@app/navigators/navigator-params"
import { GET_REGIONS } from "@app/services/graphql/queries/getRegions"
import { useStores } from "@app/models"
import { LocationService } from "@app/services/location/location"

const DEFAULT_RADIUS_METERS = 10000

type LocationDirectoryScreenProps = StackScreenProps<RootNavigatorParamList, "LocationDirectory">

interface RegionListItem {
  id: string
  name: string
  slug?: string
}

interface RegionGroup {
  letter: string
  regions: RegionListItem[]
}

interface RegionsQueryResponse {
  regions?: {
    data: Array<{
      id: string
      attributes?: {
        name?: string | null
        slug?: string | null
      } | null
    }>
  }
}

const geocodeRegion = async (name: string) => {
  const response = await fetch(
    `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
      `${name}, Sverige`
    )}&limit=1`
  )
  if (!response.ok) throw new Error("failed_geocode")
  const [first] = await response.json()
  if (!first?.lat || !first?.lon) throw new Error("missing_coords")
  return {
    latitude: Number(first.lat),
    longitude: Number(first.lon)
  }
}

export const LocationDirectoryScreen = observer((props: LocationDirectoryScreenProps) => {
  const { setLocation, setRadius, updateSelectedLocation, setFindLocationOnMount } = useStores()
  const locationService = useMemo(() => new LocationService(), [])

  const { data, loading, error, refetch } = useQuery<RegionsQueryResponse>(GET_REGIONS, {
    notifyOnNetworkStatusChange: true
  })
  const [selectedRegionId, setSelectedRegionId] = useState<string | null>(null)

  const compare = useCallback(
    (a: string, b: string) => a.localeCompare(b, "sv", { sensitivity: "base" }),
    []
  )

  const regionGroups: RegionGroup[] = useMemo(() => {
    const map = new Map<string, RegionListItem[]>()
    const entries = data?.regions?.data ?? []

    entries.forEach(({ id, attributes }) => {
      const rawName = attributes?.name?.trim()
      if (!id || !rawName) return
      const parts = rawName
        .split("/")
        .map((part) => part.trim())
        .filter(Boolean)
      parts.forEach((part, partIdx) => {
        const itemId = parts.length > 1 ? `${id}-${partIdx}` : id
        const letter = part.charAt(0).toLocaleUpperCase("sv-SE")
        if (!map.has(letter)) map.set(letter, [])
        map.get(letter).push({ id: itemId, name: part, slug: attributes?.slug })
      })
    })

    return Array.from(map.entries())
      .sort(([a], [b]) => compare(a, b))
      .map(([letter, regions]) => ({
        letter,
        regions: regions.sort((a, b) => compare(a.name, b.name))
      }))
  }, [compare, data])

  const navigateHome = () => {
    props.navigation.navigate("Tabs", {
      screen: "Home",
      params: { screen: "Offers" }
    })
  }

  const handleBack = () => {
    if (props.navigation.canGoBack()) {
      props.navigation.goBack()
    } else {
      navigateHome()
    }
  }

  const handleRegionPress = useCallback(
    async (region: RegionListItem) => {
      try {
        setSelectedRegionId(region.id)
        const coordinate = await geocodeRegion(region.name)
        const addresses = await locationService.reverseGeocode(coordinate)
        const address = addresses[0] ?? {}
        const displayName = region.name
        const newLocation = {
          latitude: coordinate.latitude,
          longitude: coordinate.longitude,
          ...address,
          name: displayName,
          city: displayName,
          region: displayName
        }

        setLocation(newLocation)
        setRadius(DEFAULT_RADIUS_METERS)
        navigateHome()
        await updateSelectedLocation(newLocation)
      } catch (err) {
        Alert.alert("Något gick fel", "Kunde inte uppdatera platsen. Försök igen.")
        __DEV__ && console.log("Failed to set region location", err)
      } finally {
        setSelectedRegionId(null)
      }
    },
    [locationService, navigateHome, setLocation, setRadius, updateSelectedLocation]
  )

  const renderRegions = () =>
    regionGroups.map(({ letter, regions }) => (
      <View key={letter} style={SECTION}>
        <Text style={LETTER} preset="bold" text={letter} />
        <View style={CHIPS}>
          {regions.map((region) => {
            const isLoadingSelection = region.id === selectedRegionId
            return (
              <Pressable
                key={region.id}
                style={[CHIP, isLoadingSelection && CHIP_DISABLED]}
                onPress={() => handleRegionPress(region)}
                disabled={isLoadingSelection}
              >
                {isLoadingSelection ? (
                  <ActivityIndicator size="small" color={color.palette.gray4} />
                ) : (
                  <Text style={CHIP_TEXT} text={region.name} />
                )}
              </Pressable>
            )
          })}
        </View>
      </View>
    ))

  return (
    <Screen style={ROOT} preset="scroll">
      <View style={HEADER}>
        <Button preset="back" onPress={handleBack}>
          <Feather style={textPresets.back} name="chevron-left" />
          <Text style={textPresets.back} text="Tillbaka" />
        </Button>
        <Text preset="headerLarge" text="Hitta rabatter i din region" />
        <Text style={SUBTITLE} text="Här hittar du rabatter baserat på regioner." />
      </View>

      {loading && (
        <View style={LOADING}>
          <ActivityIndicator size="large" color={color.primary} />
        </View>
      )}
      {!loading && error && (
        <View style={ERROR_BOX}>
          <Text text="Kunde inte hämta regionerna just nu." />
          <Button preset="link" text="Försök igen" onPress={() => refetch()} />
        </View>
      )}
      {!loading && !error && <View style={SECTIONS}>{renderRegions()}</View>}
    </Screen>
  )
})

const ROOT: ViewStyle = {
  backgroundColor: color.palette.white
}

const HEADER: ViewStyle = {
  paddingHorizontal: spacing[4],
  paddingTop: spacing[5],
  paddingBottom: spacing[3]
}

const SUBTITLE: TextStyle = {
  marginTop: spacing[2],
  color: color.palette.gray4
}

const MAP_BUTTON: ViewStyle = {
  marginTop: spacing[3],
  alignSelf: "flex-start"
}

const LOADING: ViewStyle = {
  paddingVertical: spacing[5]
}

const ERROR_BOX: ViewStyle = {
  paddingHorizontal: spacing[4],
  paddingVertical: spacing[3]
}

const SECTIONS: ViewStyle = {
  paddingHorizontal: spacing[4],
  paddingBottom: spacing[6]
}

const SECTION: ViewStyle = {
  borderBottomWidth: 1,
  borderBottomColor: color.palette.gray2,
  paddingVertical: spacing[3]
}

const LETTER: TextStyle = {
  marginBottom: spacing[2],
  color: color.palette.gray5
}

const CHIPS: ViewStyle = {
  flexDirection: "row",
  flexWrap: "wrap"
}

const CHIP: ViewStyle = {
  paddingHorizontal: spacing[3],
  paddingVertical: spacing[2],
  borderRadius: 999,
  backgroundColor: color.palette.gray1,
  marginRight: spacing[2],
  marginBottom: spacing[2]
}

const CHIP_DISABLED: ViewStyle = {
  opacity: 0.6
}

const CHIP_TEXT: TextStyle = {
  color: color.palette.gray5
}
